import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { coverScale } from '../artwork-texture.js';
import { getCloudsea } from '../cloudsea-assets.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. La scène est une photo.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'cloudsea',
  family: 'photographic',
  danceability: 0.22,
  requiresTier: 0,
  energyLevel: 0.10,
  bpmMin: 40,
  bpmMax: 120,
  movement: 0.10,
  tonality: 0.92,
  enabled: true
};

// CLOUDSEA : une mer de nuages vue d'un sommet, de l'aube au lever, à partir
// de DEUX PHOTOS ALIGNÉES (tools/build-cloudsea.mjs : alignement SIFT +
// RANSAC sur la roche, résidu 0,64 px ; masques ciel / pics / premier plan,
// les nuages étant le reste ; champ d'écoulement ; position du soleil ;
// plaques de nuages où la roche voisine est remplacée par du nuage). Les
// fichiers : cloudsea-dawn.jpeg est l'aube sans soleil, cloudsea-sunrise.jpeg
// le lever avec le soleil (le brief les annonçait inversés).
//
// L'ÉCOULEMENT (R8 : seuls les nuages bougent, ciel, pics et premier plan sont
// la photo fixe) : flow-map à deux phases. Phase A échantillonne
// uv - flux(uv) x fract(φ) x D, phase B la même avec φ + 0,5, poids
// triangulaires (chaque phase est invisible quand elle se remet à zéro). D =
// 0,6 % de la largeur par période ; la période va de 8 s (calme) à 4 s
// (intense) selon adapt.intensity, et φ est ACCUMULÉE (aucun saut quand la
// période change). Le flux vient du build : dérive de gauche à droite, plus
// rapide au premier plan, presque nulle à l'horizon, sa composante entrante
// retirée au bord des pics (le nuage les contourne). On échantillonne les
// PLAQUES (la roche proche y est déjà du nuage) : un échantillon décalé ne
// va jamais chercher de la roche.
//
// LE LEVER (R20, lib/scene-clock.js, cycle de 20 s qui démarre à 0,15 soit
// 3 s) : couleur = mix(aube, lever, smoothstep(T - 0,1, T + 0,1, p)), p =
// temps de scène / 18 s, T = distance au soleil + bruit, avancée là où le
// lever éclaire (luminance du lever - de l'aube : dessus des nuages, faces
// des pics tournées vers le soleil). À 18 s tout est doré et le reste ;
// seuls les nuages, les voiles et les rayons vivent encore (on lit
// clock.elapsed, pas la phase). Le disque est procédural : il monte de
// derrière l'horizon nuageux (masqué par le ciel), visible dès ~4,6 s,
// au-dessus du seuil de bloom.
//
// LES VOILES (R14, demi-résolution, on stocke une densité) : fbm étiré
// horizontalement, deux échelles (voile large, filaments), dérive un peu
// plus rapide que les nuages, attirés au pied des pics (canal B du flux),
// alpha = 1 - exp(-densité x k). Densité selon adapt.intensity.
// LES RAYONS (même passe) : 12 pas de la position vers le soleil (6 au tier
// 0) ; la part du chemin qui n'est pas derrière la roche (pics, premier
// plan) : le ciel ouvert donne une lueur douce, un pic sur le chemin y
// découpe son ombre (les pics coupent les rayons). Ils vivent dans la brume au-dessus de la mer et grandissent avec
// le lever.
//
// PREMIER PLAN (R13) : corps noir pur, liseré doré de ~2,5 px sur le bord
// tourné vers le soleil (différence du masque doux vers le soleil :
// proportionnelle à l'orientation), qui apparaît avec le lever ; l'herbe du
// haut du masque oscille de 1 px.
//
// CADRAGE (R18) : couvrant (coverScale), recadrage ancré sur le soleil (au
// tiers gauche quand l'écran est plus étroit que l'image). Au format 16:9,
// le soleil est à x 0,31 : la pochette centrée ne le cache pas.
//
// Aucune impulsion (R9, 0,22). Repli : sans le lever, l'aube seule (ni
// propagation, ni disque, ni rayons, ni liseré) ; sans l'aube,
// indisponible (cloudsea-assets.js retire le visualiseur).
// Tier 0 : l'écoulement sur une seule image (l'aube, teintée par le rapport
// moyen lever/aube des nuages selon la carte T), rayons à 6 pas.
// Debug : ?cloudseat=<s> (temps de scène), ?cloudseaflow=1 (le champ),
// ?cloudseamask=1 (les masques), ?cloudseaveils=0.
//
// precision highp (exception documentée) : les UV de l'écoulement bougent de
// fractions de pixel ; en mediump fp16 (la TV) le pas serait de 0,7 px et
// l'écoulement avancerait par crans.

const TIER = {
  0: { single: true, rays: 6 },
  1: { single: false, rays: 12 },
  2: { single: false, rays: 12 }
};
const DEFAULT_TIER = 2;
const CYCLE = 20;                 // s, the scene cycle (R20)
const START = 0.15;               // of the cycle: the gap already pink
const GOLD_AT = 18;               // s: gold over the whole image
const DISP = 0.006;               // of the width, per flow period
const PERIOD_CALM = 8, PERIOD_HOT = 4;
const DISC_FROM = 4, DISC_TO = 18; // s, the disc's course
const SAFE_FLOOR = 0.55;
const EXPOSURE = 0.85;           // R10, see the shader
const VEIL_SPEED = 0.0016;        // uv/s (the clouds: up to 0.001)

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const smooth = (a, b, x) => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };

// value noise, R and G independent; sampled at (floor + smoothed fract) so
// one fetch is a smooth value noise
function makeNoiseTexture() {
  const N = 256, data = new Uint8Array(N * N * 4);
  let s = 24680;
  for (let i = 0; i < N * N * 4; i++) { s = (s * 16807) % 2147483647; data[i] = s & 255; }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter; t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;

const COMMON = `
  precision highp float;
  uniform sampler2D uMask, uFlow, uNoise;
  uniform vec2 uFit, uCenter;
  uniform vec2 uImgPx;            // the image, px
  uniform vec2 uSun;              // the sun's centre in the image, uv (y up)
  uniform float uHorizon;         // the skyline under the sun, uv y
  varying vec2 vUv;
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return texture2D(uNoise, (i + f + 0.5) / 256.0).r;
  }
  float vnoiseG(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return texture2D(uNoise, (i + f + 0.5) / 256.0).g;
  }
  vec2 imageUv() { return (vUv - 0.5) * uFit + uCenter; }
`;

// the half-resolution pass: R = veil density / 2, G = rays
const halfFragment = (steps) => COMMON + `
  uniform float uVeilT, uVeilDens, uVeilsOn, uRaysOn;
  float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
  void main() {
    vec2 p = imageUv();
    vec3 m = texture2D(uMask, p).rgb;
    float clouds = clamp(1.0 - m.r - m.g - m.b, 0.0, 1.0);
    float asp = uImgPx.x / uImgPx.y;
    float veil = 0.0;
    if (uVeilsOn > 0.5 && clouds > 0.003) {
      float pull = texture2D(uFlow, p).b;
      // perspective: 0 at the horizon, 1 at the bottom of the frame
      float persp = clamp((uHorizon - p.y) / uHorizon, 0.03, 1.0);
      float fy = 1.0 / (0.40 + persp);           // rows tighten toward the horizon (x 2.5 at most)
      vec2 q = vec2(p.x * asp - uVeilT, p.y);    // the veils' frame drifts (R14), not the noise UVs
      // stretched 2.5:1 at most: more and they read as scan lines
      float wide = 0.62 * vnoise(q * vec2(1.6, 4.0 * fy) + 3.1) + 0.38 * vnoise(q * vec2(3.4, 8.5 * fy) + 9.7);
      float fil = 0.6 * vnoiseG(q * vec2(4.5, 11.0 * fy) + 1.7) + 0.4 * vnoiseG(q * vec2(9.0, 22.0 * fy) + 5.3);
      float d = smoothstep(0.30, 0.85, wide) * 0.55 + smoothstep(0.45, 0.90, fil) * smoothstep(0.35, 0.75, wide) * 0.55;
      veil = d * (0.30 + 1.30 * pull) * clouds * sqrt(persp) * uVeilDens;
    }
    float rays = 0.0;
    if (uRaysOn > 0.5) {
      // march from here to the sun: the share of the path NOT behind rock
      // (peaks, ledge). Open sky gives a smooth glow; a peak on the path
      // casts its shaft of shadow through it. (An emission summed near the
      // disc gave blobs, clipped into a hard ellipse.)
      float j = hash(gl_FragCoord.xy);
      vec2 d = uSun - p;
      float vis = 0.0;
      for (int i = 0; i < ${steps}; i++) {
        float t = (float(i) + j) / ${steps}.0;
        vec3 mq = texture2D(uMask, p + d * t).rgb;
        vis += 1.0 - mq.g - mq.b;
      }
      vis /= ${steps}.0;
      float rs = length((p - uSun) * vec2(asp, 1.0));
      // in the haze over the sea: fades up into the sky and down into the clouds
      float above = p.y - uHorizon;
      float band = above > 0.0 ? exp(-above / 0.22) : exp(above / 0.10);
      rays = vis * vis * exp(-rs / 0.32) * band * (1.0 - m.g - m.b);
    }
    gl_FragColor = vec4(clamp(veil * 0.5, 0.0, 1.0), clamp(rays, 0.0, 1.0), 0.0, 1.0);
  }
`;

const mainFragment = (single) => COMMON + `
  uniform sampler2D uDawn, uRise, uDawnP, uRiseP, uHalf;
  uniform vec2 uScreenPx;
  uniform float uPhase;           // the flow's accumulated phase
  uniform vec2 uDisp;             // D, image uv (x, y)
  uniform float uP;               // the sunrise's progress (scene time / 18 s)
  uniform float uHasRise;
  uniform float uSunR;            // the disc's radius, image uv y
  uniform float uDiscY;           // its centre now, image uv y
  uniform float uMaxD;
  uniform vec3 uGold;             // tier 0: the sunrise / dawn ratio on the clouds
  uniform float uTime, uRimK, uRaysK, uVeilK, uDebug;
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  float lum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    vec2 p = imageUv();
    vec2 px = uFit / uScreenPx;                     // one screen pixel, image uv
    vec3 m = texture2D(uMask, p).rgb;               // r sky, g peaks, b foreground
    float clouds = clamp(1.0 - m.r - m.g - m.b, 0.0, 1.0);
    vec4 fl = texture2D(uFlow, p);
    vec2 f = (fl.rg - 0.5) * 2.0;
    f.y = -f.y;                                     // the build's y points down
    if (uDebug > 1.5) { gl_FragColor = vec4(0.5 + 0.5 * f * 4.0, fl.b, 1.0) * (0.25 + 0.75 * clouds); return; }
    if (uDebug > 0.5) { vec3 dc = texture2D(uDawn, p).rgb; gl_FragColor = vec4(dc * 0.35 + vec3(m.r * 0.3, m.g, m.b) * 0.65 + vec3(0.0, 0.0, 0.0) + clouds * vec3(0.25), 1.0); return; }

    // the fixed photo, and the light's arrival T
    vec3 dawn = texture2D(uDawn, p).rgb;
    vec3 rise = uHasRise > 0.5 ? texture2D(uRise, p).rgb : dawn;
    float lit = clamp((lum(rise) - lum(dawn)) * 2.5, 0.0, 1.0);
    float dist = length((p - uSun) * vec2(uImgPx.x / uImgPx.y, 1.0)) / uMaxD;
    float n = vnoise(p * vec2(9.0, 6.0) + 4.2) - 0.5;
    float T = 0.22 + 0.62 * clamp(dist + 0.12 * n - 0.16 * lit, 0.0, 1.0);
    float w = uHasRise > 0.5 ? smoothstep(T - 0.1, T + 0.1, uP) : 0.0;
    vec3 col = mix(dawn, rise, w);

    // the clouds flow (two phases, triangular weights)
    if (clouds > 0.003) {
      float fa = fract(uPhase), fb = fract(uPhase + 0.5);
      float wa = 1.0 - abs(1.0 - 2.0 * fa);
      vec2 ua = p - f * uDisp * fa, ub = p - f * uDisp * fb;
      vec3 dF = texture2D(uDawnP, ua).rgb * wa + texture2D(uDawnP, ub).rgb * (1.0 - wa);
${single ? `      vec3 rF = dF * uGold;` : `      vec3 rF = uHasRise > 0.5 ? texture2D(uRiseP, ua).rgb * wa + texture2D(uRiseP, ub).rgb * (1.0 - wa) : dF;`}
      col = mix(col, mix(dF, rF, w), clouds);
    }

    // R10 (calm, 0.25 linear): the golden morning measured 0.289 at full exposure
    col *= ${EXPOSURE.toFixed(2)};
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float att = safeAtt(s);
    vec4 hv = texture2D(uHalf, vUv);
    // the veils (R14): translucent, diffuse, dawn lilac to morning gold
    float va = (1.0 - exp(-hv.r * 2.0 * uVeilK)) * att * 0.85;
    vec3 veilCol = mix(vec3(0.50, 0.47, 0.58), vec3(0.95, 0.74, 0.50), w);
    col = mix(col, veilCol, va);
    // the rays
    col += vec3(1.0, 0.72, 0.42) * hv.g * uRaysK * att;
    // the highlights (photo + veils + rays): a soft knee from 0.55 to under
    // 0.72, the bloom's threshold being 0.78. Only the disc blooms: the
    // sunrise's white sky bloomed into a blob, and the rays' bloom spilled
    // over the peaks
    float L = lum(col);
    if (L > 0.55) col *= (0.55 + 0.17 * (1.0 - exp(-(L - 0.55) / 0.17))) / L;

    // the disc, rising from behind the cloud horizon (the sky mask hides it)
    if (uHasRise > 0.5) {
      vec2 dpx = (p - vec2(uSun.x, uDiscY)) * uImgPx;
      float d = length(dpx), r = uSunR * uImgPx.y;
      float disc = 1.0 - smoothstep(r - 0.8, r + 0.8, d);
      float limb = 0.82 + 0.18 * sqrt(max(0.0, 1.0 - d * d / (r * r)));
      float halo = exp(-max(d - r, 0.0) / (r * 0.9)) * 0.30 + exp(-max(d - r, 0.0) / (r * 5.0)) * 0.10;
      float up = smoothstep(${DISC_FROM.toFixed(1)}, ${(DISC_FROM + 3).toFixed(1)}, uP * ${GOLD_AT.toFixed(1)});
      col += (vec3(1.0, 0.92, 0.74) * 1.25 * disc * limb + vec3(1.0, 0.70, 0.40) * halo * up) * m.r * att;
    }

    // the ledge (R13): pure black, a golden rim on the edge facing the sun,
    // the grass on top swaying by 1 px
    if (m.b > 0.002 || texture2D(uMask, p + vec2(0.0, 2.0 * px.y)).b > 0.002) {
      float top = 1.0 - texture2D(uMask, p + vec2(0.0, 10.0 * px.y)).b;
      vec2 q = p + vec2(px.x * top * sin(uTime * 1.6 + p.x * 90.0 + 0.7 * sin(uTime * 0.6 + p.x * 23.0)), 0.0);
      float fore = texture2D(uMask, q).b;
      vec2 dir = normalize((uSun - q) * uImgPx);
      float ahead = texture2D(uMask, q + dir * px * 2.0).b;
      // the contour's outward normal (the soft mask's gradient), and how much
      // it faces the sun: only those edges light (never the whole outline)
      vec2 gr = vec2(texture2D(uMask, q + vec2(px.x * 1.5, 0.0)).b - texture2D(uMask, q - vec2(px.x * 1.5, 0.0)).b,
                     texture2D(uMask, q + vec2(0.0, px.y * 1.5)).b - texture2D(uMask, q - vec2(0.0, px.y * 1.5)).b);
      float facing = dot(-gr / max(length(gr), 1e-4), dir);
      float rim = clamp(fore - ahead, 0.0, 1.0) * fore * smoothstep(0.55, 0.95, facing);
      col = mix(col, vec3(0.0), fore);
      col += vec3(1.0, 0.74, 0.40) * 0.70 * rim * uRimK * w;
    }
    col += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

export class CloudseaVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    const tier = o.qualityTier != null ? o.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const t0 = parseFloat(urlParam('cloudseat'));
    this._forcedT = Number.isFinite(t0) && t0 >= 0 ? t0 : null;
    const dbg = urlParam('cloudseamask') === '1' ? 1 : urlParam('cloudseaflow') === '1' ? 2 : 0;
    this._debug = dbg;
    this._veilsOn = urlParam('cloudseaveils') !== '0';
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: CYCLE, start: START });
    this._tOff = this._forcedT != null ? this._forcedT - this._clock.elapsed : 0;
  }

  get postProcessing() { return { bloomStrength: 0.3, bloomThreshold: 0.78 }; }

  init(scene) {
    this._scene = scene;
    this._assets = getCloudsea();
    this._size = new THREE.Vector2();
    this._time = 0; this._phase = 0; this._veilT = 0;
    this._noise = makeNoiseTexture();
    const A = this._assets;
    const man = A ? A.manifest : null;
    const hasRise = !!(A && A.rise && A.risePlate && man.sun);
    this._hasRise = hasRise;
    const sun = hasRise ? man.sun : { x: 0.31, y: 0.33, r: 0.018, horizon: 0.385 };
    this._sun = sun;
    const imgPx = new THREE.Vector2(man ? man.size[0] : 1376, man ? man.size[1] : 768);
    const asp = imgPx.x / imgPx.y;
    // the farthest corner from the sun (T's normalisation)
    let maxD = 0;
    for (const [cx, cy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) maxD = Math.max(maxD, Math.hypot((cx - sun.x) * asp, cy - sun.y));
    const common = {
      uMask: { value: A ? A.masks : null }, uFlow: { value: A ? A.flow : null }, uNoise: { value: this._noise },
      uFit: { value: new THREE.Vector2(1, 1) }, uCenter: { value: new THREE.Vector2(0.5, 0.5) },
      uImgPx: { value: imgPx }, uSun: { value: new THREE.Vector2(sun.x, 1 - sun.y) }, uHorizon: { value: 1 - sun.horizon }
    };
    this._halfU = Object.assign({}, common, {
      uVeilT: { value: 0 }, uVeilDens: { value: 1 }, uVeilsOn: { value: this._veilsOn ? 1 : 0 }, uRaysOn: { value: hasRise ? 1 : 0 }
    });
    const g = man && man.cloudGold ? man.cloudGold : [1, 1, 1];
    this._u = Object.assign({}, common, {
      uDawn: { value: A ? A.dawn : null }, uRise: { value: hasRise ? A.rise : null }, uDawnP: { value: A ? A.dawnPlate : null },
      uRiseP: { value: hasRise ? A.risePlate : null }, uHalf: { value: null }, uScreenPx: { value: new THREE.Vector2(1280, 720) },
      uPhase: { value: 0 }, uDisp: { value: new THREE.Vector2(DISP, DISP * asp) }, uP: { value: 0 }, uHasRise: { value: hasRise ? 1 : 0 },
      uSunR: { value: sun.r }, uDiscY: { value: 0 }, uMaxD: { value: maxD }, uGold: { value: new THREE.Vector3(g[0], g[1], g[2]) },
      uTime: { value: 0 }, uRimK: { value: 0 }, uRaysK: { value: 0 }, uVeilK: { value: 1 }, uDebug: { value: this._debug },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    this._rt = new THREE.WebGLRenderTarget(4, 4, { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._u.uHalf.value = this._rt.texture;
    const mat = (u, fs) => new THREE.ShaderMaterial({ uniforms: u, vertexShader: quadVertex, fragmentShader: fs, depthTest: false, depthWrite: false });
    this._halfMat = mat(this._halfU, halfFragment(this._layout.rays));
    this._mainMat = mat(this._u, mainFragment(this._layout.single));
    this._halfScene = new THREE.Scene();
    this._halfQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._halfMat);
    this._halfQuad.frustumCulled = false;
    this._halfScene.add(this._halfQuad);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10); this._ortho.position.z = 1;
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._mainMat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    this._quad.visible = !!A;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);
    this._sceneT = this._clock.elapsed + this._tOff;
    this._apply();
  }

  // R18: cover the screen, the crop anchored on the sun (the left third when
  // the screen is narrower than the image)
  _frame(aspect) {
    const sz = this._u.uImgPx.value;
    const fit = coverScale(aspect, sz.x / sz.y);
    this._u.uFit.value.copy(fit);
    const sx = this._sun.x, sy = 1 - this._sun.y;
    const cx = fit.x < 1 ? Math.min(1 - fit.x / 2, Math.max(fit.x / 2, sx + fit.x / 6)) : 0.5;
    // taller-than-wide crops keep the sun at 60 % of the height
    const cy = fit.y < 1 ? Math.min(1 - fit.y / 2, Math.max(fit.y / 2, sy - 0.1 * fit.y)) : 0.5;
    this._u.uCenter.value.set(cx, cy);
  }

  _render(renderer) {
    if (!this._assets) return;
    renderer.getDrawingBufferSize(this._size);
    const W = Math.max(2, this._size.x), H = Math.max(2, this._size.y);
    const aspect = W / H;
    if (aspect !== this._aspect) { this._aspect = aspect; this._frame(aspect); }
    this._u.uScreenPx.value.set(W, H);
    const hw = Math.max(2, Math.round(W / 2)), hh = Math.max(2, Math.round(H / 2));
    if (this._rt.width !== hw || this._rt.height !== hh) this._rt.setSize(hw, hh);
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._rt);
    renderer.render(this._halfScene, this._ortho);
    renderer.setRenderTarget(prev);
  }

  // the scene's state at scene time t
  _apply() {
    const t = this._sceneT, u = this._u, sun = this._sun;
    u.uP.value = clamp01(t / GOLD_AT);
    // the disc: from just under the cloud horizon to its place in the photo
    const x = clamp01((t - DISC_FROM) / (DISC_TO - DISC_FROM)), e = 1 - (1 - x) * (1 - x);
    const y0 = sun.horizon + 1.15 * sun.r, y1 = sun.y - 0.3 * sun.r;
    this._discY = y0 + (y1 - y0) * e;
    u.uDiscY.value = 1 - this._discY;
    u.uRaysK.value = this._hasRise ? 0.30 * smooth(4.5, 14, t) : 0;
    u.uRimK.value = this._hasRise ? smooth(5, 14, t) : 0;
  }

  update(deltaTime, profile) {
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    const a = this._adapt.update(dt, profile);
    this._clock.update(dt);
    if (dt === 0) return;                         // paused: the frame holds
    this._time += dt;
    // no pulse (R9): the intensity sets the flow's period and the veils
    const period = PERIOD_CALM + (PERIOD_HOT - PERIOD_CALM) * a.intensity;
    this._period = period;
    this._phase = (this._phase + dt / period) % 1024;
    this._veilT += dt * VEIL_SPEED * (0.8 + 0.4 * a.intensity);
    this._sceneT = this._clock.elapsed + this._tOff;
    const u = this._u;
    u.uPhase.value = this._phase;
    u.uTime.value = this._time;
    this._halfU.uVeilT.value = this._veilT;
    this._halfU.uVeilDens.value = 0.7 + 0.6 * a.intensity;
    this._apply();
  }

  get probe() {
    const a = this._adapt.get();
    return {
      ready: !!this._assets, rise: this._hasRise, tier: this._tier, scene: +this._sceneT.toFixed(2), p: +this._u.uP.value.toFixed(3),
      discY: this._discY, sun: this._sun, period: this._period, phase: this._phase, intensity: +a.intensity.toFixed(2),
      rays: this._u.uRaysK.value, rim: this._u.uRimK.value, fit: this._u.uFit.value.toArray(), center: this._u.uCenter.value.toArray()
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); }
    if (this._halfQuad) this._halfQuad.geometry.dispose();
    if (this._mainMat) this._mainMat.dispose();
    if (this._halfMat) this._halfMat.dispose();
    if (this._rt) this._rt.dispose();
    if (this._noise) this._noise.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}
